import {useMe} from '@/haligator-factory';
import {Badge} from '@chakra-ui/react';
import {useHalCollection} from '@synako/haligator';
import {useMemo} from 'react';

interface BadgeConversationsNonLuesProps {
  /**
   * Restreint le compteur aux conversations de ce client (popin fiche client / mode commercial),
   * pour rester cohérent avec les listes client-scopées. Sans `clientId` : compteur global
   * (toutes mes participations) — cas du menu latéral BO et de l'interface client.
   */
  clientId?: string;
  /** Position absolue du badge (nécessite un parent en position relative) */
  position?: 'absolute' | 'static';
  top?: string;
  right?: string;
}

/**
 * Badge affichant le nombre de conversations avec des messages non lus.
 * Utilisé sur le bouton flottant et l'onglet "en cours" de la liste.
 */
export const BadgeConversationsNonLues: React.FC<
  BadgeConversationsNonLuesProps
> = ({clientId, position = 'absolute', top = '-2px', right = '-2px'}) => {
  const me = useMe();

  // Récupérer les participations pour compter les conversations avec messages non lus.
  // Restreint au client si fourni (même clé de cache que les listes de la popin → pas de fetch en plus),
  // sinon compteur global (toutes mes participations).
  const participationsNav = clientId
    ? me.follow('participations-conversation', {client_id: clientId})
    : me.follow('participations-conversation');
  const {items} = useHalCollection(participationsNav);

  // Compter le nombre de conversations en cours avec des messages non lus
  const nombreConversationsNonLues = useMemo(() => {
    if (!items) return 0;
    return items.filter(
      (p) =>
        p.data.conversation_statut === 'en_cours' &&
        p.data.messages_non_lus > 0,
    ).length;
  }, [items]);

  if (nombreConversationsNonLues === 0) {
    return null;
  }

  return (
    <Badge
      position={position}
      top={position === 'absolute' ? top : undefined}
      right={position === 'absolute' ? right : undefined}
      bg='red.500'
      color='white'
      borderRadius='full'
      fontSize='xs'
      fontWeight='bold'
      minW='20px'
      h='20px'
      display='flex'
      alignItems='center'
      justifyContent='center'
      px={1}
    >
      {nombreConversationsNonLues}
    </Badge>
  );
};
